// WHO DID IT, for the activity space (pure functions: no React, no DOM).
//
// An activity row records who made the change in ONE field, `activity_events.source`:
// the label of the connected tool (set from its user agent at the door: Claude, ChatGPT,
// Cursor, Cline, CLI, web, mcp), `netti` for netti's own folds, `innernet agent` for the
// artifact engine (netti's earlier name), `innernet` for the rooms' pulse, and `agent:<slug>`
// when an agent keeps the pen. The river names it in every row's meta line; the source chips
// group the loaded rows by it, so a chip filters exactly the rows that carry its word
// (law_mapped_controls). Many spellings, one source: `appOf` (lib/you/private) is the house's
// one reading of a raw source string, the same one the private ring uses, so a chip here and
// "who has seen what" there never disagree about which app is which.
//
// Two things differ from `appOf`, both on purpose:
//   • the words are the chip's (short: "CLI", "mac app", never "a connected tool, unnamed");
//   • a row with no named tool says "innernet" in the river (ActivityItem has always said so),
//     so the chip says it too, and netti's own moves (netti, innernet agent) are one source.

import { appOf } from "../../lib/you/private";

export interface ActivitySource {
  /** the stable id of the source: what a chip is pressed by and a filter is kept as */
  key: string;
  /** the chip's word, and the one the row's meta line says */
  label: string;
  /** what `BrandIcon` is asked for: its real logo, else a monogram of the label */
  brand: string;
}

export interface SourceCount extends ActivitySource {
  count: number;
  /** the newest `created_at` among its rows */
  latest: string;
}

/** innernet's own moves: they sit after the tools, which is what is coming in */
const OWN: ReadonlySet<string> = new Set(["netti", "innernet"]);

/** the source one raw `activity_events.source` belongs to (null / empty / "mcp": innernet) */
export function sourceOf(raw: string | null | undefined): ActivitySource {
  const app = appOf(raw);
  switch (app.key) {
    case "agent": // 'innernet agent': netti, before it had its name
    case "netti":
      return { key: "netti", label: "netti", brand: "netti" };
    case "mcp": // a connected tool that did not say who it is: the river calls it innernet
    case "unknown":
    case "innernet":
      return { key: "innernet", label: "innernet", brand: "innernet" };
    case "cli":
      return { key: "cli", label: "CLI", brand: "cli" };
    case "web":
      return { key: "web", label: "web", brand: "web" };
    case "extension":
      return { key: "extension", label: "extension", brand: "browser" };
    case "desktop":
      return { key: "desktop", label: "mac app", brand: "innernet" };
    case "api":
      return { key: "api", label: "api", brand: "innernet" };
    default:
      // a named tool (Claude, Cursor …) or an agent ('agent:<slug>', labelled by its slug)
      return { key: app.key, label: app.label, brand: app.label };
  }
}

/** a kept source key, said again as a source (every key reads back as itself) */
export const describeSource = (key: string): ActivitySource => sourceOf(key);

/**
 * The distinct sources in a set of rows, each with how many rows and the newest one. A source
 * with one row still has its entry. Tools come first, the most active leading; innernet's own
 * (netti, innernet) follow. Only what the rows actually carry: nothing is invented.
 */
export function sourcesIn(rows: ReadonlyArray<{ source: string | null; created_at: string }>): SourceCount[] {
  const by = new Map<string, SourceCount>();
  for (const r of rows) {
    const s = sourceOf(r.source);
    const hit = by.get(s.key);
    if (hit) {
      hit.count += 1;
      if (r.created_at > hit.latest) hit.latest = r.created_at;
    } else {
      by.set(s.key, { ...s, count: 1, latest: r.created_at });
    }
  }
  return [...by.values()].sort(
    (a, b) =>
      Number(OWN.has(a.key)) - Number(OWN.has(b.key)) ||
      b.count - a.count ||
      (a.latest < b.latest ? 1 : a.latest > b.latest ? -1 : 0) ||
      a.label.localeCompare(b.label),
  );
}

/** does this row belong to the source? `null` is no filter at all: everything does */
export function inSource(row: { source: string | null }, key: string | null): boolean {
  return key === null || sourceOf(row.source).key === key;
}

/** the rows of one source, in the order given (`null`: all of them) */
export function fromSource<T extends { source: string | null }>(rows: readonly T[], key: string | null): T[] {
  return key === null ? [...rows] : rows.filter((r) => sourceOf(r.source).key === key);
}

/**
 * The chips to draw: the sources in the rows, plus the one that is on when no loaded row
 * carries it (a filter kept from earlier in the session) so it can always be turned off.
 */
export function chipSources(rows: ReadonlyArray<{ source: string | null; created_at: string }>, on: string | null): ActivitySource[] {
  const found: ActivitySource[] = sourcesIn(rows);
  if (on !== null && !found.some((s) => s.key === on)) found.push(describeSource(on));
  return found;
}

/** the one line an empty filter says, in the source's own word */
export function nothingFrom(label: string, looking: boolean): string {
  return looking ? `looking further back for ${label}.` : `nothing from ${label} yet.`;
}
